import { useState, useSyncExternalStore } from "react"
import { z } from "zod"
import {
  type ColumnLayout,
  type ColumnSizing,
  type ColumnWidths,
  gridTemplate,
  MIN_WIDTH,
  withWidths,
} from "@/lib/column-widths"

export type ColumnArrange<S extends string> = {
  fixed: readonly S[]
  widthOf: (slot: S) => number
  onResize: (slot: S, width: number) => void
  onResizeEnd: (slot: S, width: number) => void
  onReorder: (slot: S, target: S) => void
}

type Stored = { order: string[]; widths: Record<string, number> }

const EMPTY: Stored = { order: [], widths: {} }

const storedSchema = z
  .object({
    order: z.array(z.string()).catch([]),
    widths: z.record(z.string(), z.number().min(MIN_WIDTH)).catch({}),
  })
  .catch(EMPTY)

const listeners = new Set<() => void>()

const cache = new Map<string, { raw: string | null; stored: Stored }>()

function storageKey(layout: { id: string }): string {
  return `columns:${layout.id}`
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function readRaw(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function parse(raw: string | null): Stored {
  if (raw === null) return EMPTY
  try {
    return storedSchema.parse(JSON.parse(raw))
  } catch {
    return EMPTY
  }
}

function read(key: string): Stored {
  const raw = readRaw(key)
  const hit = cache.get(key)
  if (hit && hit.raw === raw) return hit.stored
  const stored = parse(raw)
  cache.set(key, { raw, stored })
  return stored
}

function write(key: string, stored: Stored): void {
  try {
    localStorage.setItem(key, JSON.stringify(stored))
  } catch {
    cache.set(key, { raw: readRaw(key), stored })
  }
  for (const listener of listeners) listener()
}

function useStored(key: string): Stored {
  return useSyncExternalStore(subscribe, () => read(key))
}

function interleave<T extends string>(
  primary: readonly T[],
  secondary: readonly T[],
): T[] {
  const result = [...primary]
  secondary.forEach((item, index) => {
    if (result.includes(item)) return
    const before = secondary
      .slice(0, index)
      .reverse()
      .find((candidate) => result.includes(candidate))
    const at = before === undefined ? 0 : result.indexOf(before) + 1
    result.splice(at, 0, item)
  })
  return result
}

function moveSlot<T extends string>(
  slots: readonly T[],
  slot: T,
  target: T,
): T[] {
  const rest = slots.filter((candidate) => candidate !== slot)
  const at = slots.indexOf(target)
  return [...rest.slice(0, at), slot, ...rest.slice(at)]
}

function arrangeSlots<S extends string>(
  slots: readonly S[],
  order: readonly string[],
  fixed: readonly S[],
): S[] {
  const movable = slots.filter((slot) => !fixed.includes(slot))
  const known = movable
    .filter((slot) => order.includes(slot))
    .sort((a, b) => order.indexOf(a) - order.indexOf(b))
  const queue = interleave(known, movable)
  return slots.map((slot) =>
    fixed.includes(slot) ? slot : (queue.shift() ?? slot),
  )
}

function storedWidths<S extends string>(
  slots: readonly S[],
  stored: Stored,
  fixed: readonly S[],
): ColumnWidths<S> {
  const widths: ColumnWidths<S> = {}
  for (const slot of slots) {
    const width = stored.widths[slot]
    if (width !== undefined && !fixed.includes(slot)) widths[slot] = width
  }
  return widths
}

export function useColumnSizing<S extends string>(
  columns: readonly S[],
  sizing: ColumnSizing<S>,
): ColumnSizing<S> {
  const stored = useStored(storageKey(sizing))
  const slots = [...columns, ...sizing.extras]
  return withWidths(sizing, storedWidths(slots, stored, sizing.pinned ?? []))
}

export function useColumnLayout<S extends string>(
  slots: readonly S[],
  layout: ColumnLayout<S>,
): { slots: S[]; template: string; arrange: ColumnArrange<S> } {
  const key = storageKey(layout)
  const stored = useStored(key)
  const [draft, setDraft] = useState<{ slot: S; width: number } | null>(null)
  const fixed = layout.pinned ?? []
  const ordered = arrangeSlots(slots, stored.order, fixed)
  const widths = storedWidths(ordered, stored, fixed)
  if (draft) widths[draft.slot] = draft.width

  return {
    slots: ordered,
    template: gridTemplate(ordered, layout, widths),
    arrange: {
      fixed,
      widthOf: (slot) => widths[slot] ?? layout.widths[slot],
      onResize: (slot, width) => setDraft({ slot, width }),
      onResizeEnd: (slot, width) => {
        write(key, { ...stored, widths: { ...stored.widths, [slot]: width } })
        setDraft(null)
      },
      onReorder: (slot, target) => {
        const movable = ordered.filter(
          (candidate) => !fixed.includes(candidate),
        )
        const moved = moveSlot(movable, slot, target)
        write(key, {
          ...stored,
          order: interleave<string>(moved, stored.order),
        })
      },
    },
  }
}
